import React from 'react';
import { SectionCard } from './SectionCard';
import { ChartViewSelector } from './chartView/ChartViewSelector';
import { CHART_VIEW_OPTIONS, useChartView } from './chartView/useChartView';
import {
  DefaultRankingBarList,
  RankingBarChart,
  RankingPieChart,
  buildRankingChartData,
} from './chartView/RankingCharts';
import { formatMoney, formatNumber, formatPercent } from '../../lib/formatMoney';
import { useTheme } from '../../context/ThemeContext';

function extractErrorDetail(error) {
  if (!error) {
    return null;
  }

  if (error.message) {
    const msg = error.message.toLowerCase();
    if (msg.includes('invoiceapiconfig') || msg.includes('not configured')) {
      return 'Backend invoice API is not configured. Check INVOICE_API env vars.';
    }
    if (msg.includes('credential') || msg.includes('401')) {
      return 'Invoice API rejected credentials. Check username/password.';
    }
    if (msg.includes('not found') || msg.includes('404')) {
      return 'Invoice API endpoint not found. Check base URL.';
    }
    if (msg.includes('rate limit') || msg.includes('429')) {
      return 'Invoice API rate limited. Please retry in a moment.';
    }
    if (msg.includes('reach') || msg.includes('network') || msg.includes('503')) {
      return 'Cannot reach invoice API. Check backend service and network.';
    }
    return error.message;
  }

  return 'An unknown error occurred while fetching top events.';
}

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-6">
      <div className="relative h-8 w-8">
        <div className="absolute inset-0 rounded-full border-2 border-slate-200 border-t-[#201C35] animate-spin" />
      </div>
      <p className="text-[11px] text-slate-600">Fetching top events...</p>
    </div>
  );
}

export function TopEventsPanel({ rows, isLoading, error, comparison, filters = {}, variant = 'detailed', currencyCode = 'DKK' }) {
  const { chartPalette } = useTheme();
  const isCompact = variant === 'compact';
  const eventLimit = isCompact ? 3 : 10;
  const chartViewOptions = React.useMemo(
    () => (isCompact ? CHART_VIEW_OPTIONS.filter((option) => option.value !== 'bar') : CHART_VIEW_OPTIONS),
    [isCompact]
  );
  const [chartView, setChartView] = useChartView('panelChartView.topEvents', 'bar', chartViewOptions);
  const headerAction = (
    <ChartViewSelector
      value={chartView}
      onChange={setChartView}
      disabled={isLoading}
      options={chartViewOptions}
    />
  );
  const topRows = rows.slice(0, eventLimit);
  const leadingEvent = topRows[0] ?? null;
  const allRevenueZero = topRows.length > 0 && topRows.every((row) => Number(row.totalRevenue || 0) <= 0);

  const initialMetric = allRevenueZero ? 'invoiceCount' : 'totalRevenue';

  const [metric, setMetric] = React.useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('topEvents.metric') : null;
      if (saved === 'totalRevenue' || saved === 'invoiceCount') return saved;
    } catch (e) {
      // ignore
    }
    return initialMetric;
  });

  React.useEffect(() => {
    if (allRevenueZero && metric === 'totalRevenue') {
      setMetric('invoiceCount');
      return;
    }

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('topEvents.metric', metric);
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [allRevenueZero, metric]);

  const chartMetric = metric === 'totalRevenue'
    ? { key: 'totalRevenue', label: 'Revenue' }
    : { key: 'invoiceCount', label: 'Invoices' };

  const formatMetricValue = (value) => (
    chartMetric.key === 'totalRevenue'
      ? formatMoney(value, { currency: currencyCode })
      : formatNumber(value)
  );

  const comparisonTone =
    typeof comparison?.percentageChange === 'number'
      ? comparison.percentageChange >= 0
        ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
        : 'text-rose-700 bg-rose-50 border-rose-100'
      : 'text-slate-600 bg-slate-50 border-slate-200';

  const summary = React.useMemo(() => {
    if (!topRows.length) {
      return null;
    }

    const totalRevenue = topRows.reduce((sum, row) => sum + row.totalRevenue, 0);
    const totalInvoices = topRows.reduce((sum, row) => sum + row.invoiceCount, 0);

    return {
      totalRevenue,
      totalInvoices
    };
  }, [topRows]);

  const alternateChartData = buildRankingChartData(topRows, 'eventName', chartMetric.key, chartPalette);
  const alternateValueLabel = chartMetric.label;

  return (
    <SectionCard
      title="Top Events"
      description={isCompact ? 'Top events at a glance' : `Top ${eventLimit} events ranked by revenue contribution`}
      headerAction={headerAction}
    >
      <div className="space-y-3">
        {!error && (isLoading || topRows.length > 0) ? (
          <>
            <div
              className={`flex flex-col gap-2 border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] sm:flex-row sm:items-center sm:justify-between ${isCompact || chartView !== 'default' ? 'hidden' : ''}`}
              aria-live="polite"
            >
              <span className="shrink-0 uppercase tracking-[0.1em] text-slate-500">Period change</span>

              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className={`rounded-none border px-2 py-0.5 font-semibold ${comparisonTone}`} role="status" aria-label={`Period change ${formatPercent(comparison?.percentageChange, { decimals: 1 })}`}>
                  {formatPercent(comparison?.percentageChange, { decimals: 1 })}
                </span>

                <div
                  className="rounded-md border border-slate-200 bg-white px-1 py-0.5"
                  role="group"
                  aria-label="Chart metric toggle"
                  tabIndex={-1}
                  onKeyDown={(e) => {
                    // Allow ArrowLeft/ArrowRight/Home/End to switch metric for keyboard users
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                      e.preventDefault();
                      if (e.key === 'Home' || e.key === 'ArrowLeft') {
                        setMetric('totalRevenue');
                        const btn = document.getElementById('top-events-toggle-revenue');
                        btn?.focus();
                      } else {
                        setMetric('invoiceCount');
                        const btn = document.getElementById('top-events-toggle-invoices');
                        btn?.focus();
                      }
                    }
                  }}
                >
                  <button
                    id="top-events-toggle-revenue"
                    type="button"
                    onClick={() => setMetric('totalRevenue')}
                    className={`px-2 py-1 text-[11px] font-medium ${metric === 'totalRevenue' ? 'bg-slate-100' : 'bg-white'}`}
                    aria-pressed={metric === 'totalRevenue'}
                    aria-controls="top-events-chart"
                    aria-label="Show revenue metric"
                    disabled={isLoading}
                  >
                    Revenue
                  </button>
                  <button
                    id="top-events-toggle-invoices"
                    type="button"
                    onClick={() => setMetric('invoiceCount')}
                    className={`ml-1 px-2 py-1 text-[11px] font-medium ${metric === 'invoiceCount' ? 'bg-slate-100' : 'bg-white'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-pressed={metric === 'invoiceCount'}
                    aria-controls="top-events-chart"
                    aria-label="Show invoices metric"
                    disabled={isLoading}
                  >
                    Invoices
                  </button>
                </div>

                <div aria-live="polite" className="sr-only" id="top-events-metric-announcer">
                  {metric === 'totalRevenue' ? 'Showing revenue metric' : 'Showing invoices metric'}
                </div>
              </div>
            </div>

            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <p className="sr-only">
                  Top Events shows the five highest-revenue events for the selected period. Leading event is {leadingEvent?.eventName} with revenue {formatMoney(leadingEvent?.totalRevenue ?? 0, { currency: currencyCode })}.
                </p>

                <p className={`text-[10px] leading-4 text-slate-500 ${isCompact || chartView !== 'default' ? 'hidden' : ''}`}>
                  Top events currently contribute <span className="font-semibold text-[#201C35]">{formatMoney(summary.totalRevenue, { currency: currencyCode })}</span>{' '}
                  across <span className="font-semibold text-[#201C35]">{formatNumber(summary.totalInvoices)}</span> invoices.
                </p>

                {allRevenueZero && !isCompact && chartView === 'default' ? (
                  <p className="text-[10px] leading-4 text-slate-500">
                    Revenue values are zero for the selected range, so the chart is showing invoice counts for visibility.
                  </p>
                ) : null}

                {chartView === 'bar' ? (
                  <RankingBarChart
                    data={alternateChartData}
                    currencyCode={currencyCode}
                    compact={isCompact}
                    valueLabel={alternateValueLabel}
                    formatValue={formatMetricValue}
                    leadingTitle="Leading event"
                    leadingCaption="Primary contributor"
                    ariaLabel="Bar chart of top events"
                  />
                ) : chartView === 'pie' ? (
                  <RankingPieChart
                    data={alternateChartData}
                    currencyCode={currencyCode}
                    compact={isCompact}
                    valueLabel={alternateValueLabel}
                    formatValue={formatMetricValue}
                    leadingTitle="Leading event"
                    leadingCaption="Primary contributor"
                    ariaLabel="Pie chart of top events"
                  />
                ) : (
                  <DefaultRankingBarList
                    data={alternateChartData}
                    formatValue={formatMetricValue}
                  />
                )}

                {!isCompact && chartView === 'default' && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-[11px] text-slate-700" aria-label="Top events detail table">
                    <caption className="sr-only">Top five events with invoice count, units sold, and revenue.</caption>
                    <thead>
                      <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                        <th scope="col" className="py-1.5 pr-2 font-semibold">Event</th>
                        <th scope="col" className="py-1.5 px-2 text-right font-semibold">Invoices</th>
                        <th scope="col" className="py-1.5 px-2 text-right font-semibold">Units</th>
                        <th scope="col" className="py-1.5 pl-2 text-right font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRows.map((row) => (
                        <tr
                          key={row.key}
                          className="border-b border-slate-100 last:border-b-0"
                          tabIndex={0}
                          role="button"
                          aria-label={`${row.eventName || 'Unknown Event'}: ${formatNumber(row.invoiceCount)} invoices, ${formatNumber(row.quantity, { decimals: 1 })} units, revenue ${formatMoney(row.totalRevenue, { currency: currencyCode })}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const ann = document.getElementById('top-events-announcer');
                              if (ann) ann.textContent = `${row.eventName || 'Unknown Event'}, ${formatNumber(row.invoiceCount)} invoices, ${formatMoney(row.totalRevenue, { currency: currencyCode })}`;
                            }
                          }}
                          onClick={() => {
                            const ann = document.getElementById('top-events-announcer');
                            if (ann) ann.textContent = `${row.eventName || 'Unknown Event'}, ${formatNumber(row.invoiceCount)} invoices, ${formatMoney(row.totalRevenue, { currency: currencyCode })}`;
                          }}
                        >
                          <td className="py-1.5 pr-2 font-medium text-[#201C35]">{row.eventName || 'Unknown Event'}</td>
                          <td className="py-1.5 px-2 text-right">{formatNumber(row.invoiceCount)}</td>
                          <td className="py-1.5 px-2 text-right">{formatNumber(row.quantity, { decimals: 1 })}</td>
                          <td className="py-1.5 pl-2 text-right font-semibold text-[#201C35]">{formatMoney(row.totalRevenue, { currency: currencyCode })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
                <div id="top-events-announcer" className="sr-only" aria-live="polite" />
              </>
            )}
          </>
        ) : null}

        {error ? (
          <div className="space-y-1.5 rounded-sm border border-red-200 bg-red-50 px-2.5 py-2" role="alert">
            <p className="text-[11px] font-semibold text-red-900">Unable to load top events</p>
            <p className="text-[10px] leading-4 text-red-700">{extractErrorDetail(error)}</p>
            {(filters.start || filters.end) && (
              <p className="text-[10px] leading-3 text-red-600 opacity-75">
                Filter: {filters.start} to {filters.end}
              </p>
            )}
          </div>
        ) : null}

        {!isLoading && !error && !topRows.length ? (
          <p className="text-[11px] text-slate-600">No top events found for the active filters.</p>
        ) : null}
      </div>
    </SectionCard>
  );
}
